import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useO5DanteV1DeadMessageQueryServiceDeadMessageGet } from '@interxfi/api-client/generated/hooks/o5-dante-v1-dead-message-query-service-dead-message-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { getShortenedId } from '@interxfi/design-system/components/format/id/shared';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import type { Tab } from '@interxfi/design-system/components/tabs/shared';
import { Tabs } from '@interxfi/design-system/components/tabs/tabs';
import { Typography } from '@interxfi/design-system/components/typography';
import type { O5DanteV1DeadMessageState } from '@interxfi/types/generated/api';
import { O5DeadMessageEntityMenu } from '../../../../../../../features/o5/dead-letter/o5-dead-message-entity-menu';
import { O5DeadMessageEventSummary } from '../../../../../../../features/o5/dead-letter/o5-dead-message-event';
import { getO5DeadMessageStateListItems } from '../../../../../../../features/o5/dead-letter/o5-dead-message-list-items';
import { O5DeadMessageStatusPill } from '../../../../../../../features/o5/dead-letter/o5-dead-message-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_o5/_o5-required/tech-ops/dead-message/_dead-message/$messageId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `dead-message-${params.messageId}`,
        label: `Message ${getShortenedId(params.messageId)}`,
        to: `/tech-ops/dead-message/${params.messageId}`,
        suffix: <CopyButton content={params.messageId} label="Copy Message ID" tooltip={{ copy: params.messageId, copied: 'Copied Message ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Dead Letter'),
  component: O5DeadMessageDetail,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load dead letter', 'view this dead letter');

function getListItems(data: O5DanteV1DeadMessageState | undefined) {
  const items = getO5DeadMessageStateListItems(data);
  const initialVersion = items.data.notification.message;
  const currentVersion = items.data.currentVersion?.message ?? initialVersion;

  function getMessageListGroups(messageItems: typeof initialVersion) {
    return [
      {
        id: 'main',
        items: [
          messageItems.messageId,
          messageItems.sourceApp,
          messageItems.sourceEnv,
          messageItems.grpcService,
          messageItems.grpcMethod,
          messageItems.destinationTopic,
          messageItems.timestamp,
          ...(messageItems.request?.replyTo ? [{ ...messageItems.request.replyTo, title: 'Reply To (Request)' }] : []),
          ...(messageItems.reply?.replyTo ? [{ ...messageItems.reply.replyTo, title: 'Reply To (Reply)' }] : []),
        ],
      },
      {
        id: 'body',
        title: 'Body',
        items: [messageItems.body.typeUrl, messageItems.body.encoding, messageItems.body.value],
      },
    ] as const satisfies ReadonlyListGroup[];
  }

  return {
    left: [
      {
        id: 'notification',
        items: [items.data.notification.deathId, items.data.notification.handlerApp, items.data.notification.handlerEnv],
      },
      {
        id: 'problem',
        title: 'Problem',
        items: [
          items.data.notification.problem.typeName,
          ...match(items.data.notification.problem)
            .with({ unhandledError: P.not(P.nullish) }, ({ unhandledError }) => [unhandledError.value.error])
            .otherwise(() => []),
        ],
      },
      {
        id: 'infra',
        title: 'Infrastructure',
        items: [items.data.notification.infra.type, items.data.notification.infra.metadata],
      },
    ] as const satisfies ReadonlyListGroup[],
    right: [items.metadata.createdAt, items.metadata.updatedAt, items.messageId] as const satisfies ReadonlyListItem[],
    currentVersion: getMessageListGroups(currentVersion),
    notificationVersion: getMessageListGroups(initialVersion),
  } as const;
}

type MessageVersionTab = 'notification' | 'current';

function O5DeadMessageDetail() {
  const { messageId } = Route.useParams();
  const request = { messageId } as const;
  const [activeTab, setActiveTab] = useState<MessageVersionTab>('current');
  const { data, error, isPending } = useO5DanteV1DeadMessageQueryServiceDeadMessageGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.deadMessage), [data?.deadMessage]);

  const tabs: Tab<MessageVersionTab>[] = useMemo(
    () => [
      {
        value: 'current',
        label: 'Current',
        children: <AppReadonlyListStack groups={listItems.currentVersion} titleGap={4} />,
      },
      {
        value: 'notification',
        label: 'Notification',
        children: <AppReadonlyListStack groups={listItems.notificationVersion} titleGap={4} />,
      },
    ],
    [listItems.currentVersion, listItems.notificationVersion],
  );

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <O5DeadMessageStatusPill isPending={showPending} size="large" status={data?.deadMessage?.status} />
          </HorizontalStack>
          <O5DeadMessageEntityMenu messageId={messageId} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <O5DeadMessageEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Notification</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Message</Typography.Level3>

          <Tabs activeTab={activeTab} label="Version" onTabChange={setActiveTab} tabs={tabs} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
